import {isNullOrUndefined} from "@qntm-code/utils";
import {Component, EventEmitter, Input, OnChanges, OnInit, Output, SimpleChanges, ViewChild} from '@angular/core';
import {ConfirmationService, SelectItem} from 'primeng/api';

import {plainToClass} from 'class-transformer';

import {GrowlService, UserService} from '@app/core';

import {FileExplorerDisplay} from '@app/shared/classes/common';
import {DocAttribute, DocFile, FilePermission, FilePermissionSettingType} from '@app/shared/classes/doc-file';
import {DocAttrGroup, DocType} from "@app/shared/classes/doc-type";
import {DocumentType} from '@app/shared/enums/document-type.enum';
import {Format} from "@app/shared/enums/format.enum";
import {Transaction} from '@app/shared/enums/txn-type.enum';
import {CommonDocRepoService} from '@app/shared/services/common-doc-repo.service';

import {CommonTransformationService} from "@app/shared/services/common-transformation.service";
import {RestrictedSolutionManagerService} from '@app/shared/services/restricted-solution-manager.service';
import {WorklistDataService} from "@app/main/worklist/worklist-data.service";
import {FileExplorerService} from './file-explorer.service';
import {GrowlType, PrimeNgConfig, PrimeNgSupportService, Roles, TableColumn, TableFormat} from "@app/shared";
import {FileUpload} from "primeng/fileupload/fileupload";

/**
 * @author: Archana Ananad and Jin Chun (April 2018)
 * @desc: This component enables user to upload, replace, and delete files stored in the OneLynk Document Repository.
 */
@Component({
  selector: 'app-file-explorer',
  templateUrl: './file-explorer.component.html',
  styleUrls: ['./file-explorer.component.css'],
  providers: [ConfirmationService, FileExplorerService, WorklistDataService]
})

export class FileExplorerComponent implements OnInit, OnChanges {

  //TODO: Get the input from user, for displaying file settings

  @Input() txnType: Transaction;                    // transaction type enum
  @Input() txnTypeId: number;                   // transaction type id; NOT supported for Template (OrgPolicy/OrgDocument) processing
  @Input() refId: number;                       // reference id; transaction type/id and refId combination makes up a unique transaction
  @Input() userId: number;                      // userId for txnType == NewHireOfferLetter, OnboardPolicyAck, OnboardDocumentAck
  @Input() documentType: DocumentType;          // required for simple file upload
  @Input() docTypeFilter: DocumentType;         // Display only files which match the doc type
  @Input() displayMode: FileExplorerDisplay;    // Dialog for simple file upload or FileExplorer for full file explorer
  @Input() fileName: string;                    // if provided along with displayMode==Dialog, then the fileName will replace the name of selected file
  @Input() editable: boolean;                   // if true, file explorer is editable
  @Input() displayUploadDialog: boolean;        // if true and display==FileExplorerDisplay.Dialog, then show the file upload dialog
  @Input() disableUpload: boolean;              // if true, disable the upload from the component display; use the "upload" flag below to save the docFiles
  @Input() upload: boolean;                     // if true and txnType/txnTypeId and refId exist, then save docFiles. this attribute requires disableUpload=true
  @Input() forceApproverCheck: boolean;         // If true, check if the context user is an approver from the backend (For specific transaction and refId)
  @Input() acceptableFileType: string;
  @Input() maxFileSize: number;

  @Input() showOwner: boolean = false;
  @Input() showModifiedDate: boolean = false;

  @Input() doNotDisplayAdditionalSaveFilesButton: boolean = false;
  @Input() useTransactionSpecificDocTypes: boolean = false;                   //If true, retrieve only doctypes grouped for the specific transaction

  @Output() uploadDialogHandler: EventEmitter<boolean> = new EventEmitter();  // handler to close file upload dialog for displayMode == Dialog
  @Output() uploadHandler: EventEmitter<boolean> = new EventEmitter();        // handler to save selected files (docFiles) when displayMode == FileExplorer
  @Output() uploadReadyHandler: EventEmitter<boolean> = new EventEmitter();   // handler to enable parent component's save button
  @Output() selectedFiles?: EventEmitter<any[]> = new EventEmitter();         // handler to close file upload dialog for displayMode == Dialog

  // constant declaration for html/template
  FileExplorerDisplay: typeof FileExplorerDisplay = FileExplorerDisplay;
  Format: typeof Format = Format;
  PrimeNgConfig = PrimeNgConfig;
  MaxFileSize: number = 15728640;                // max file size supported by backend; if changed, make sure to change the
                                                 // backend as well as nginx configuration

  // allow pdf, text, microsoft, text and image files for upload
  AcceptableFileType = "text/*, image/*, application/pdf, application/rtf, application/x-rtf, application/msword, application/vnd.ms-excel, application/vnd.ms-powerpoint, application/vnd.openxmlformats-officedocument.wordprocessingml.document, application/vnd.openxmlformats-officedocument.wordprocessingml.template, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet, application/vnd.openxmlformats-officedocument.spreadsheetml.template, application/vnd.openxmlformats-officedocument.presentationml.presentation, application/vnd.openxmlformats-officedocument.presentationml.template, application/vnd.oasis.opendocument.text";

  txnTypeOrId: Transaction | number;            // set to txnTypeId (if exist) or txnType
  docTypes: DocType[];
  docTypeDropdown: SelectItem[];                // a list of doc types to selected by the user when displayMode == FileExplorer

  uploadAllEnabled: boolean = false;            // set to true, if all files have file types selected

  docFiles: DocFile[] = [];                     // array of files on the table. initialized to empty array.
  selectedFile: DocFile;                        // selected row/file on the table
  docTypeId: number;                            // when docName is provided as input
  selectedDocAttrs: Array<DocAttribute> = [];   // collected doc attributes (for the selectedFile)
  displayFileInfoDialog: boolean = false;       // if true, show file info dialog
  tableColumns: TableColumn[];                  // column headers and fields to display on the table
  attributeTableColumns: TableColumn[];

  selectedFileInBrowse: File;                   // selected file from Browse File dialog
  docFile: DocFile;                             // file to be uploaded, set only when displayMode == FileExplorerDisplay.Dialog

  //File settings properties
  displayFileSettings: boolean = false;
  selectedDocFileId: number;
  FilePermissionSettingType: typeof FilePermissionSettingType = FilePermissionSettingType;
  contextUserApprover: boolean = false;       // If true, include approver permissions during processing permissions

  helpOverview: boolean = false;
  helpUploadFile: boolean = false;
  helpReplaceFile: boolean = false;

  @ViewChild('fileUpload') fileUpload: FileUpload;


  constructor(private confirmationService: ConfirmationService, private transform: CommonTransformationService, private dataService: CommonDocRepoService, public user: UserService,
              private growl: GrowlService, private fileExplorerService: FileExplorerService, private primeNgSerivce: PrimeNgSupportService,
              private workListService: WorklistDataService, private solutionManagerService: RestrictedSolutionManagerService) {
  }


  /**
   * @desc load doc types and a file list for the input txnType/txnTypeId and refId
   * @returns this.docTypes - retrieved list of doc types for the txnType/txnTypeId
   * @returns this.docFiles - retrieved list of files for the txnType/txnTypeId
   */
  ngOnChanges(changes: SimpleChanges): void {

    console.debug(`file-explorer ngOnChanges: txnType=${this.txnType}  transactionId=${this.txnTypeId}  refId=${this.refId}  documentType=${this.documentType}  display=${this.displayMode}
    editable=${String(this.editable)}  uploadDialog=${String(this.displayUploadDialog)}`);

    // txnTypeOrId holds either txnTypeId or txnType. txnTypeOrId will be used throughout this component especially when calling data services
    if (this.txnTypeId) {
      this.txnTypeOrId = this.txnTypeId;
    } else {
      this.txnTypeOrId = this.txnType;
    }

    // default userId for user specific txnType
    if (!this.userId) {
      this.userId = this.user.info.userId;
    }

    //If forceApprover check is true, check if the context user is approver for the current transaction.
    if (this.forceApproverCheck && this.txnTypeOrId && this.refId) {
      this.workListService.checkIfContextUserIsApprover(this.txnTypeOrId, this.refId).subscribe(res => {
        // FixMe: Angular12Upgrade - check this res.get() works after changing the signature of checkIfContextUserIsApprover() to use Map in order to sync with backend
        this.contextUserApprover = res.get("isUserApprover");
      });
    }

    if (this.txnTypeOrId && this.displayMode == FileExplorerDisplay.FileExplorer && !this.upload) {
      // load doc types and files when showing file explorer except when save is not set
      this.getFileListForTransaction(this.txnTypeOrId, this.refId);
    } else if (this.refId && this.txnTypeOrId && this.upload) {
      // save is true, upload the selected files in docFiles
      this.onClickUploadFiles();
    }
  }

  ngOnInit() {
    this.initTableColumns();
    this.getDocTypes();
    this.processFileConfig();
  }

  /**
   * @desc sets file configuration properties such as allowed file types, maxFileSize if provided as input, else use default values
   */
  processFileConfig(): void {
    if (this.acceptableFileType) {
      this.AcceptableFileType = this.acceptableFileType;
    }

    if (this.maxFileSize) {
      this.MaxFileSize = this.maxFileSize;
    }
  }

  /**
   * @desc load a list of doc types for user to select
   * @returns this.docTypes - a list of available doc types
   */
  getDocTypes():void {

    if (!this.docTypes) {
      let transaction = null;
      if (this.useTransactionSpecificDocTypes) {
        transaction = this.txnType;
      }
      this.dataService.getDocTypes(transaction).subscribe(res => {
        this.docTypes = plainToClass(DocType, res.list);
        // Build dropdown with non-system doctypes
        const filteredDocTypes = this.docTypes.filter(type => type.systemDocType == false);
        this.docTypeDropdown = this.transform.convertToDropDown("name", filteredDocTypes, "id", true);
      });
    }
  }

  /**
   * @desc load a list of files associated with txnType and refId
   * @param txnType - txnType or txnTypeId - transaction type
   * @param refId - reference id of the transaction
   * @returns this.docFiles = a list of files loaded and to be displayed on the data table
   */
  getFileListForTransaction(txnType: Transaction | number, refId: number):void {

    if (txnType && refId) {
      if ((txnType == Transaction.OnboardingPolicyAck || txnType == Transaction.OnboardingDocumentAck) || txnType == Transaction.NewHireOfferLetter) {
        txnType = Transaction.NewHireRequest;
      }
      if(this.user.hasRole[Roles.OrgAdmin] || this.user.hasRole[Roles.CSRAdmin]) {
        this.solutionManagerService.getFileListForTransaction(txnType, refId).subscribe(res => {
          this.docFiles = plainToClass(DocFile, res.list);
          this.processDocFileList();
        });
      } else {
        this.dataService.getFileListForTransaction(txnType, refId).subscribe(res => {
          this.docFiles = plainToClass(DocFile, res.list);
          this.processDocFileList();
        });
      }
    } else if ((txnType == Transaction.OrgPolicy || txnType == Transaction.OrgDocument) && !refId) {
      //ToDo if txnTypeId of OrgPolicy or OrgDocument type is specified, template processing will not work
      this.dataService.getTemplateFiles(txnType).subscribe(res => {
        this.docFiles = plainToClass(DocFile, res.list);
        this.processDocFileList();
      });
    } else {
      this.docFiles = [];
      if (this.selectedFiles) {
        this.selectedFiles.emit(this.docFiles);
      }
      console.warn("file-explorer: cannot get file list without txnType|txnTypeId and refId");
    }
  }

  processDocFileList():void{
    this.docFiles = this.fileExplorerService.processPermissionsForFiles(this.docFiles, this.contextUserApprover);
    //If a docType filter is specified, display only files which match the document type
    if (this.docTypeFilter) {
      this.docFiles = this.fileExplorerService.filterFilesBasedOnDocType(this.docFiles, this.docTypeFilter);
    }
    if (this.selectedFiles) {
      this.selectedFiles.emit(this.docFiles);
    }
  }

  /**
   * @desc use cancelled file selection, if selected file exists, remove it from the docFiles
   */
  onClickCancelFileUploadDialog():void {

    if (this.selectedFileInBrowse) {
      // remove selected file from the docFiles array; the array should be empty
      this.docFiles = this.docFiles.filter(docFile => docFile.fileName === this.selectedFileInBrowse.name);
      this.selectedFileInBrowse = null;
    }
    this.displayUploadDialog = false;
    this.uploadDialogHandler.emit(false);
  }

  /**
   * @desc close the file upload dialog
   * @returns this.displayUploadDialog - set to false and inform the parent component about the dialog change
   */
  onClickCloseFileUploadDialog():void {

    this.displayUploadDialog = false;
    this.uploadDialogHandler.emit(false);
  }

  /**
   * @desc if all files have doc file type, it's ready for upload.
   */
  readyForUpload(docFiles: DocFile[], txnTypeOrId: Transaction|number, refId: number):void {

    this.uploadAllEnabled = this.fileExplorerService.allFilesHaveDocType(docFiles);
    this.uploadReadyHandler.emit(this.uploadAllEnabled);
  }

  /**
   * @desc load the selected file (event.files[0]) to the data table
   * @param event - containing file to load
   * @param fileToReplace - if set, this is the file in the this.docFiles array to replace with event.files[0]
   * @returns: this.docFiles - a new file (event.files[0]) added to this array
   */
  onSelectBrowseFile(event, fileToReplace?: DocFile): void {

    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
    const selectedFileToLoad: File = event.files[0];

    if(!this.fileUpload.validate(selectedFileToLoad)){
      return;
    }

    if (selectedFileToLoad && selectedFileToLoad.size > this.MaxFileSize) {
      this.growl.add(GrowlType.Warn, `The file is too large. It has to be less than ${(this.MaxFileSize / 1000000).toFixed(0)}M bytes.`);
      return;
    }
    // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment
    this.selectedFileInBrowse = event.files[0];

    // override file name if specified for displayMode=Dialog
    let fileName: string;
    if (this.displayMode === FileExplorerDisplay.Dialog && this.fileName) {
      fileName = this.fileName;
    } else {
      fileName = selectedFileToLoad.name;
    }

    // add file if no files are in the docFiles array, or file name and type combination is not in the docFiles or replacing an existing file
    if (!this.docFiles.find(file => (file.fileName === fileName)) || fileToReplace) {

      let newFile = new DocFile();
      let permission = new FilePermission();
      permission.canRead = true;
      permission.canWrite = true;
      permission.canDelete = true;
      newFile.permission = permission;

      // set docTypeId since it was specified when this component was called
      if (this.documentType && this.docTypes) {
        // docTypes should be available when this.displayMode === FileExplorerDisplay.FileExplorer
        const docType = this.docTypes.find(dt => dt.enumName === this.documentType);
        if (docType) {
          newFile.docTypeId = docType.id;
        }
      }
      newFile.fileName = selectedFileToLoad.name;
      newFile.mimeType = selectedFileToLoad.type;

      // load the file as bytearray
      this.dataService.loadDocData(newFile, selectedFileToLoad);

      if (fileToReplace) {
        // replace the existing file
        const index = this.docFiles.findIndex(file => file.id === fileToReplace.id);
        if (index >= 0) {
          newFile.id = fileToReplace.id;
          newFile.transaction = fileToReplace.transaction;
          newFile.path = fileToReplace.path;
          newFile.ownerId = fileToReplace.ownerId;
          newFile.version = fileToReplace.version;
          this.docFiles = this.primeNgSerivce.replaceByIndex(this.docFiles, newFile, index);
        }
      } else {
        if (this.displayMode === FileExplorerDisplay.Dialog) {
          // if it's a simple file upload, empty the docFiles and add the file selected
          this.docFile = newFile;
          if (this.fileName) {
            this.docFile.fileName = this.fileName;
          }
        } else {
          // add new file to the docFiles array
          this.docFiles = this.primeNgSerivce.unshift(this.docFiles, newFile);
        }

        if (this.selectedFiles) {
          this.selectedFiles.emit(this.docFiles);
        }
      }

      this.readyForUpload(this.docFiles, this.txnTypeOrId, this.refId);
      this.fileUpload.clear();
    } else {
      this.selectedFileInBrowse = null;
      this.fileUpload.clear();
      this.growl.add(GrowlType.Warn, "The file you are trying to add already exist. Please select a different file or change the file name.");
    }
  }

  helpDialogO():void {
    this.helpOverview = true;
  }

  helpDialogP():void {
    this.helpUploadFile = true;
  }

  helpDialogQ():void {
    this.helpReplaceFile = true;
  }

  /**
   * @desc upload new and existing files in the data table to backend db
   * @returns this.docFiles - latest list of files for the specified txnType/txnTypeId and refId
   */
  onClickUploadFiles():void {

    let docFilesToLoad: DocFile[] = [];

    if (this.displayMode === FileExplorerDisplay.Dialog) {
      docFilesToLoad.push(this.docFile);
    } else {
      docFilesToLoad = this.docFiles;
    }

    // filter out null and wo permission to write
    docFilesToLoad = docFilesToLoad.filter(file => (file.permission && file.permission.canWrite && (file.data != null || file.updated)));

    if (docFilesToLoad && docFilesToLoad.length > 0) {
      if (this.txnType == Transaction.OrgPolicy || this.txnType == Transaction.OrgDocument) {
        this.dataService.saveTemplateFiles(this.txnTypeOrId, docFilesToLoad).subscribe(res => {
          if (this.displayMode == FileExplorerDisplay.FileExplorer) {
            this.getFileListForTransaction(this.txnTypeOrId, this.refId);
          }
          this.growl.add(GrowlType.Success, 'Template File(s) uploaded.');
          this.onClickCloseFileUploadDialog();
        });
      } else {
        if(this.user.hasRole[Roles.OrgAdmin] || this.user.hasRole[Roles.CSRAdmin]){
          this.solutionManagerService.saveFilesForTransaction(this.txnTypeOrId, this.refId, docFilesToLoad).subscribe(res => {
            this.handleFileSaving();
          });
        } else{
          this.dataService.saveFilesForTransaction(this.txnTypeOrId, this.refId, docFilesToLoad).subscribe(res => {
            this.handleFileSaving();
          });
        }
      }
    }
  }

  handleFileSaving():void {
    if (this.upload) {
      this.upload = false;
      this.uploadHandler.emit(false);
    }

    // ToDo: if res returns updated file list, remove this line
    // this is also an issue for single file upload
    if (this.displayMode == FileExplorerDisplay.FileExplorer) {
      this.getFileListForTransaction(this.txnTypeOrId, this.refId);
    }

    this.growl.add(GrowlType.Success, 'File(s) uploaded.');
    this.onClickCloseFileUploadDialog();
  }

  /**
   * @desc update associated attribute with the selected file
   * @param file - selected file
   * @returns this.selectedDocAttrs - attributes assocated the file to edit
   */
  onClickEditFileInfo(file: DocFile):void {

    this.selectedFile = file;     // this should be done automatically...
    this.selectedDocAttrs = [];

    if (file.docTypeId) {

      const index = this.docTypes.findIndex(docType => docType && docType.id == file.docTypeId);
      let attributeGroups: DocAttrGroup[];
      if (index >= 0) {
        attributeGroups = this.docTypes[index].attributeGroups;
      }

      if (file.docAttributes && file.docAttributes.length > 0) {
        this.selectedDocAttrs = file.docAttributes;

        // add any attributes that have not be populated before with the selected doc type
        for (const attributeGroup of attributeGroups) {
          if (!this.selectedDocAttrs.find(doc => doc.attributeNameId === attributeGroup.attributeNameId)) {
            const selectedDocAttr = new DocAttribute(attributeGroup.attributeNameId, attributeGroup.attributeNameName, attributeGroup.attributeNameType);
            this.selectedDocAttrs.push(selectedDocAttr);
          }
        }
      } else {
        // no attributes were stored, populate with attributes associated with the selected doc type
        for (const attributeGroup of attributeGroups) {
          const selectedDocAttr = new DocAttribute(attributeGroup.attributeNameId, attributeGroup.attributeNameName, attributeGroup.attributeNameType);
          this.selectedDocAttrs.push(selectedDocAttr);
        }
      }
      this.displayFileInfoDialog = true;
    }
  }

  /**
   * @desc download the selected file and view it on a separate browser tab
   * @param file - selected file
   * @returns a broser tab showing the selected file
   */
  onClickViewFile(file: DocFile):void {
    this.dataService.getFileByIdThenShowOnNewTab(file.id);
  }

  /**
   * @desc download the selected file and save it user's local file system
   * @param file - selected file
   * @returns the selected file saved in user's browser download directory
   */
  onClickDownloadFile(file: DocFile):void {
    this.dataService.getFileByIdThenSave(file.id);
  }

  /**
   * @desc update the doc attributes
   */
  onClickSaveFileInfo():void {
    if (this.selectedFile && this.selectedDocAttrs.length > 0) {
      // remove attributes with null or undefined
      this.selectedFile.docAttributes = this.selectedDocAttrs.filter(attr => !isNullOrUndefined(attr.attrValue));
      this.selectedFile.updated = true;
    }
    this.readyForUpload(this.docFiles, this.txnTypeOrId, this.refId);
    this.displayFileInfoDialog = false;
  }

  /**
   * @desc close the file info dialog
   */
  onClickCloseFileInfo():void {
    this.displayFileInfoDialog = false;
  }

  /**
   * @desc: change the docTypeId
   * @param file - with docType object
   * @returns file.docTypeId - set to the selected file.docType.id
   */
  onChangeDocType(file: DocFile):void {
    file.docAttributes = null;
    file.updated = true;
    this.readyForUpload(this.docFiles, this.txnTypeOrId, this.refId);

    if (this.selectedFiles) {
      this.selectedFiles.emit(this.docFiles);
    }
  }

  /**
   * @desc delete the selected file from the data table
   * @param file - selected file to delete
   * @returns this.docFiles - the selected file removed from the backend, array and also from the table
   */
  onClickDeleteFile(file: DocFile):void {

    this.confirmationService.confirm({
      message: `Are you sure that you want to delete this file (${file.fileName})?`,
      header: 'Delete File',
      icon: 'fa fa-question-circle',
      accept: () => {
        if (file.id) {
          this.dataService.deleteFileById(file.id).subscribe(res => {
            this.docFiles = this.primeNgSerivce.deleteById(this.docFiles, file.id);
            this.growl.add(GrowlType.Success, `File (${file.fileName}) is deleted.`);
          });
        } else {
          const index = this.docFiles.findIndex(f => f.fileName == file.fileName);
          if (index >= 0) {
            this.docFiles = this.primeNgSerivce.deleteByIndex(this.docFiles, index);
            this.growl.add(GrowlType.Success, `File (${file.fileName}) is deleted.`);
          }
        }
        this.readyForUpload(this.docFiles, this.txnTypeOrId, this.refId);
      }
    });
  }

  onClickEditFileSettings(file: DocFile): void {
    this.selectedDocFileId = file.id;
    this.displayFileSettings = true;
  }

  updateFileSettings(event): void {
    this.displayFileSettings = false;
  }

  private initTableColumns(): void {
    this.tableColumns = [
      {field: 'fileName', header: 'File Name'},
      {field: 'docTypeId', header: 'File Type'},
    ];
    if (this.showOwner) {
      this.tableColumns.push({field: 'ownerName', header: 'File Owner'});
    }
    if (this.showModifiedDate) {
      this.tableColumns.push({field: 'createdOn', header: 'Created Date', format: TableFormat.Time});
      this.tableColumns.push({field: 'modifiedOn', header: 'Modified Date', format: TableFormat.Time});
    }
    this.tableColumns.push({field: 'action', header: 'Action'});

    this.attributeTableColumns = [
      {field: 'attrName', header: 'Attribute Name'},
      {field: 'attrValue', header: 'Attribute Value'},
    ];
  }

}
